import CampaignPlatformType from '@/Enums/CampaignManagement/CampaignPlatformType.enum';
import axios from 'axios';

interface Option {
  value: string | number;
  label: string;
}

interface ClientOption extends Option {
  advertisers: Option[];
}

/**
 * The filters a saved view carries (#5128). Sort, page and page size are left out
 * on purpose: a view is "which campaigns", not how the table is laid out.
 * Clients/advertisers/campaigns are stored as `{ value, label }` only, since the
 * live client option embeds its whole advertiser list, which goes stale.
 */
export interface SavedViewFilters {
  dateRangeStart?: string;
  dateRangeEnd?: string;
  search?: string;
  status?: string[];
  pacingStatus?: Option[];
  channelTypes?: Option[];
  level?: string;
  platform?: string;
  isArchived?: boolean;
  clients?: Option[];
  advertisers?: Option[];
  campaigns?: Option[];
}

export interface SavedViewConfig {
  version: 1;
  filters: SavedViewFilters;
}

export interface SavedView {
  id: number;
  name: string;
  config: SavedViewConfig;
}

const toOptions = (items: Option[] = []): Option[] => items.map(({ value, label }) => ({ value, label }));

export const toSavedViewConfig = (filters: Record<string, any>): SavedViewConfig => ({
  version: 1,
  filters: {
    dateRangeStart: filters.dateRangeStart,
    dateRangeEnd: filters.dateRangeEnd,
    search: filters.search,
    status: filters.status,
    pacingStatus: filters.pacingStatus,
    channelTypes: filters.channelTypes,
    level: filters.level,
    platform: filters.platform,
    isArchived: filters.isArchived,
    clients: toOptions(filters.clients),
    advertisers: toOptions(filters.advertisers),
    campaigns: toOptions(filters.campaigns),
  },
});

/**
 * Turns a saved view back into page filters. Clients and advertisers are matched
 * against what the user can see today, so one they lost access to is dropped
 * rather than silently filtering on nothing. Campaigns take precedence over both
 * in the request and carry no advertiser to match against, so they are dropped
 * whenever a saved client or advertiser was.
 */
export const fromSavedViewConfig = (
  config: SavedViewConfig,
  allClients: ClientOption[],
  isDV360CampaignManagementEnabled: boolean,
): SavedViewFilters & { clients: ClientOption[]; advertisers: Option[] } => {
  const saved = config?.filters ?? {};

  const clients = (saved.clients ?? [])
    .map(client => allClients.find(option => option.value === client.value))
    .filter((client): client is ClientOption => !!client);
  const availableAdvertisers = clients.flatMap(client => client.advertisers);
  const advertisers = (saved.advertisers ?? [])
    .map(advertiser => availableAdvertisers.find(option => option.value === advertiser.value))
    .filter((advertiser): advertiser is Option => !!advertiser);
  const lostAccess =
    clients.length < (saved.clients?.length ?? 0) || advertisers.length < (saved.advertisers?.length ?? 0);

  return {
    ...saved,
    clients,
    advertisers,
    campaigns: lostAccess ? [] : (saved.campaigns ?? []),
    ...(isDV360CampaignManagementEnabled ? {} : { platform: CampaignPlatformType.Omnichannel }),
  };
};

const PATH = '/campaign-management/saved-views';

export const listSavedViews = async (): Promise<SavedView[]> => {
  const response = await axios.get(PATH);
  return (response.data ?? []) as SavedView[];
};

export const createSavedView = async (name: string, config: SavedViewConfig): Promise<SavedView> => {
  const response = await axios.post(PATH, { name, config });
  return response.data as SavedView;
};

export const deleteSavedView = async (id: number): Promise<void> => {
  await axios.delete(`${PATH}/${id}`);
};
